import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../utils/api';
import SearchableSelect from './SearchableSelect';

function genMonths(count = 24) {
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i);
    return { value: d.toISOString().slice(0, 7), label: d.toLocaleString('default', { month: 'long', year: 'numeric' }) };
  });
}

const MONTHS = genMonths(24);
const CUR = MONTHS[0].value;
const SIX_AGO = MONTHS[5]?.value || CUR;

export default function ExportModal({ onClose }) {
  const [dateMode, setDateMode] = useState('single');
  const [month, setMonth] = useState(CUR);
  const [fromMonth, setFromMonth] = useState(SIX_AGO);
  const [toMonth, setToMonth] = useState(CUR);
  const [clientId, setClientId] = useState('');
  const [nodeId, setNodeId] = useState('');
  const [loading, setLoading] = useState(false);

  const { data: clients = [] } = useQuery({ queryKey: ['clients'], queryFn: () => api.get('/clients').then(r => r.data) });

  const selectedClient = useMemo(() => clients.find(c => c.id === clientId), [clients, clientId]);

  const persons = useMemo(() => {
    if (!selectedClient || selectedClient.type !== 'hierarchical') return [];
    return (selectedClient.allNodes || []).filter(n => !n.parent_id);
  }, [selectedClient]);

  function handleClientChange(cid) { setClientId(cid); setNodeId(''); }

  function fileLabel() {
    const datePart = dateMode === 'all' ? 'All_Data'
      : dateMode === 'single' ? month
      : `${fromMonth}_to_${toMonth}`;
    const clientPart = selectedClient ? selectedClient.name.replace(/[^a-zA-Z0-9]/g, '_') : 'All_Clients';
    const personNode = persons.find(p => p.id === nodeId);
    const personPart = personNode ? `_${personNode.name.replace(/[^a-zA-Z0-9]/g, '_')}` : '';
    return `8D_Business_OS_${clientPart}${personPart}_${datePart}.xlsx`;
  }

  function summaryLine() {
    const datePart = dateMode === 'all' ? 'all entries (no date filter)'
      : dateMode === 'single' ? month
      : `${fromMonth} → ${toMonth}`;
    const clientPart = selectedClient ? selectedClient.name : 'all clients';
    const personNode = persons.find(p => p.id === nodeId);
    const personPart = personNode ? `, person: ${personNode.name}` : '';
    return `${clientPart}${personPart} · ${datePart}`;
  }

  async function handleExport() {
    if (dateMode === 'range' && fromMonth > toMonth) {
      toast.error('"From" month must be before "To" month'); return;
    }
    setLoading(true);
    try {
      const params = {};
      if (dateMode === 'single') params.month = month;
      else if (dateMode === 'range') { params.fromMonth = fromMonth; params.toMonth = toMonth; }
      if (clientId) params.clientId = clientId;
      if (nodeId) params.nodeId = nodeId;

      const res = await api.get('/reports/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = fileLabel(); a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
      onClose();
    } catch {
      toast.error('Export failed — try again');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal-title">Export Excel</div>

        {/* Date mode */}
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>Date</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
          {[['single', 'Single month'], ['range', 'Date range'], ['all', 'All data']].map(([v, l]) => (
            <button key={v} className={dateMode === v ? 'btn-primary btn-sm' : 'btn btn-sm'} onClick={() => setDateMode(v)}>{l}</button>
          ))}
        </div>

        {dateMode === 'single' && (
          <div className="field">
            <label>Month</label>
            <select value={month} onChange={e => setMonth(e.target.value)}>
              {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        )}

        {dateMode === 'range' && (
          <div className="form-grid">
            <div className="field">
              <label>From</label>
              <select value={fromMonth} onChange={e => setFromMonth(e.target.value)}>
                {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div className="field">
              <label>To</label>
              <select value={toMonth} onChange={e => setToMonth(e.target.value)}>
                {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
          </div>
        )}

        {dateMode === 'all' && (
          <div style={{ fontSize: 12, color: 'var(--text2)', background: 'var(--surface2)', padding: '8px 12px', borderRadius: 'var(--radius-sm)', marginBottom: 14 }}>
            Exports every work log entry with no date restriction.
          </div>
        )}

        <hr className="divider" style={{ margin: '14px 0' }} />

        {/* Client */}
        <div className="field">
          <label>Client</label>
          <SearchableSelect value={clientId} onChange={handleClientChange} items={clients}
            getLabel={c => `${c.name}${c.type === 'hierarchical' ? ' (hierarchical)' : ''}`}
            allowClear clearLabel="All clients" placeholder="All clients" />
        </div>

        {/* Person — only visible when a hierarchical client with persons is selected */}
        {persons.length > 0 && (
          <div className="field">
            <label>Person</label>
            <select value={nodeId} onChange={e => setNodeId(e.target.value)}>
              <option value="">All persons</option>
              {persons.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}

        {/* Preview */}
        <div style={{ marginTop: 16, padding: '10px 14px', background: 'var(--surface2)', borderRadius: 'var(--radius-sm)', borderLeft: '3px solid var(--accent)' }}>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>EXPORT SCOPE</div>
          <div style={{ fontSize: 12, color: 'var(--text1)', fontWeight: 500 }}>{summaryLine()}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>📁 {fileLabel()}</div>
        </div>

        <div className="modal-actions">
          <button onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn-primary" onClick={handleExport} disabled={loading}>
            {loading ? 'Generating…' : '⬇ Export Excel'}
          </button>
        </div>
      </div>
    </div>
  );
}
